<script setup lang="ts">
import type { TaskItem } from '@Types/dashboard.types';
import TaskCompletionHero from '@Components/completion/TaskCompletionHero.vue';
import IllustrationVerifiedId
    from '@Components/completion/illustrations/IllustrationVerifiedId.vue';

defineProps<{
    task: TaskItem;
}>();

// Copy sourced verbatim from certifid-vision/app/i18n/locales/en.json
// (idv.wizard.completed.* / idv.wizard.success.*). Inlined here — no i18n
// in dashboard-mfe today — so the wording stays in one place when content
// design iterates.
const protectsItems = [
    {
        title: 'Prevents impersonation',
        description: 'Confirms that only you can participate in key steps of the transaction.',
    },
    {
        title: 'Reduces wire fraud risk',
        description: 'Helps ensure funds and documents go to the right people.',
    },
    {
        title: 'Protects your property transfer',
        description: 'Adds an extra layer of security during closing.',
    },
];
</script>

<template>
    <div class="vicv">
        <!-- Landscape ID-card render reads best vertically centered in the
             hero band rather than anchored at the bottom. -->
        <TaskCompletionHero illustration-align="center">
            <template #illustration>
                <IllustrationVerifiedId />
            </template>
            Your identity has been verified
        </TaskCompletionHero>

        <div class="vicv__body">
            <p class="vicv__message">
                Thank you for taking this important step! You’re helping protect
                this entire transaction from fraud.
            </p>

            <div class="vicv__divider" />

            <app-collapse class="vicv__accordion" initial-state="closed">
                <template #trigger="slotProps">
                    <button
                        type="button"
                        class="vicv__accordion-trigger"
                        :aria-expanded="slotProps?.isOpen ?? false"
                    >
                        <span>How this protects you</span>
                        <span
                            class="vicv__accordion-caret"
                            :class="{ 'vicv__accordion-caret--open': slotProps?.isOpen }"
                        >
                            <app-icon icon="cd-ic-caret-down" />
                        </span>
                    </button>
                </template>
                <div class="vicv__accordion-body">
                    <div v-for="item in protectsItems" :key="item.title" class="vicv__accordion-item">
                        <h4 class="vicv__accordion-item-title">{{ item.title }}</h4>
                        <p class="vicv__accordion-item-desc">{{ item.description }}</p>
                    </div>
                </div>
            </app-collapse>

            <div class="vicv__divider" />

            <app-collapse class="vicv__accordion" initial-state="closed">
                <template #trigger="slotProps">
                    <button
                        type="button"
                        class="vicv__accordion-trigger"
                        :aria-expanded="slotProps?.isOpen ?? false"
                    >
                        <span>How your data is protected</span>
                        <span
                            class="vicv__accordion-caret"
                            :class="{ 'vicv__accordion-caret--open': slotProps?.isOpen }"
                        >
                            <app-icon icon="cd-ic-caret-down" />
                        </span>
                    </button>
                </template>
                <div class="vicv__accordion-body">
                    <p class="vicv__accordion-lede">
                        CertifID takes your privacy seriously at every step of
                        identity verification.
                    </p>
                    <p class="vicv__accordion-p">
                        The information you provide is securely protected and
                        used only to verify your identity.
                    </p>
                    <p class="vicv__accordion-p">
                        Your data is encrypted and stored using strong,
                        industry-standard security protections. Only authorized
                        people involved in your transaction can access it, and
                        that access is closely monitored to keep your
                        information safe.
                    </p>
                </div>
            </app-collapse>

            <div class="vicv__divider" />

            <div class="vicv__support">
                <p class="vicv__support-title">
                    Still have questions about identity verification?
                </p>
                <p class="vicv__support-line">
                    Email our support team at:
                    <a href="mailto:support@certifid.com">support@certifid.com</a>
                </p>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.vicv {
    display: flex;
    flex-direction: column;

    // Slightly wider than the shared hero default — the ID-card illustration
    // has more internal detail and benefits from the extra width.
    :deep(.tch__illustration) {
        width: 192px;

        @media (min-width: 768px) {
            width: 208px;
        }
    }

    &__body {
        display: flex;
        flex-direction: column;
        padding: 20px $portal-section-px 0;

        @media (min-width: 768px) {
            padding: 20px 32px 0;
        }
    }

    &__message {
        font-size: $text-portal-body-lg;
        color: $portal-text;
        line-height: 1.55;
        margin: 0 0 20px;
    }

    &__divider {
        height: 1px;
        background: $portal-border;
    }

    &__accordion {
        width: 100%;
    }

    &__accordion-trigger {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        width: 100%;
        padding: 20px 0;
        background: transparent;
        border: 0;
        cursor: pointer;
        font: inherit;
        color: $portal-text;
        font-size: $text-portal-body-lg;
        font-weight: 700;
        text-align: left;

        &:hover {
            color: var(--ch-primary, #156FBE);
        }
    }

    &__accordion-caret {
        flex-shrink: 0;
        display: inline-flex;
        color: $portal-text-mid;
        transition: transform 0.2s ease;

        :deep(svg) {
            width: 16px;
            height: 16px;
        }

        &--open {
            transform: rotate(180deg);
        }
    }

    &__accordion-body {
        padding-bottom: 20px;
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    &__accordion-item {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    &__accordion-item-title {
        font-size: $text-portal-body-lg;
        font-weight: 700;
        color: $portal-text;
        margin: 0;
        line-height: 1.4;
    }

    &__accordion-item-desc {
        font-size: $text-portal-body-lg;
        color: $portal-text;
        line-height: 1.55;
        margin: 0;
    }

    &__accordion-lede {
        font-size: $text-portal-body-lg;
        font-weight: 700;
        color: $portal-text;
        line-height: 1.55;
        margin: 0;
    }

    &__accordion-p {
        font-size: $text-portal-body-lg;
        color: $portal-text;
        line-height: 1.55;
        margin: 0;
    }

    &__support {
        margin-top: 20px;
        border: 1px solid $portal-border;
        border-radius: 6px;
        padding: 10px 12px;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    &__support-title {
        font-size: $text-portal-small;
        font-weight: 600;
        color: $portal-text-mid;
        margin: 0;
    }

    &__support-line {
        font-size: $text-portal-small;
        color: $portal-text-mid;
        line-height: 1.35;
        margin: 0;

        a {
            color: var(--ch-primary, #156FBE);
            text-decoration: underline;
        }
    }
}
</style>
